import {
  Button,
  Card,
  CardContent,
  EmptyState,
  Separator,
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "@pushpress/pushpress-ui";
import { useDailySchedule } from "./useDailySchedule";
import { ScheduleItem } from "./ScheduleItem";
import { ScheduleItemSkeleton } from "./ScheduleItemSkeleton";
import { AppContext } from "../../../context/app";
import { useContext, useEffect } from "react";
import { useGoogleSyncAuthorization } from "../../../apis/googleSyncApi.hooks";
import { AlertCircle, ArrowRight } from "lucide-react";
import { cn } from "../../../utils/css";
import { useNavigate } from "../../../hooks/useNavigate";
import { useAnalytics } from "../../../components/providers/AnalyticsProvider";

const errorMessages = {
  dailySchedule: "Error loading schedule",
  googleCalendar: "Error loading Google Calendar",
  googleSync: "Error with Google Sync",
};

function MySchedule() {
  const { user } = useContext(AppContext);
  const { trackEvent } = useAnalytics();
  const {
    dailySchedule,
    isDailyScheduleLoading,
    userDailySchedule,
    userDailyScheduleLoading,
    isDailyScheduleError,
    isGoogleCalendarError,
  } = useDailySchedule();

  const navigate = useNavigate();

  const { data: isGoogleCalendarConnected, isError: isGoogleSyncError } =
    useGoogleSyncAuthorization(user.uuid);

  const loadingSkeleton = (
    <div className="tw-space-y-4">
      <ScheduleItemSkeleton />
      <ScheduleItemSkeleton />
      <ScheduleItemSkeleton />
      <ScheduleItemSkeleton />
      <ScheduleItemSkeleton />
      <ScheduleItemSkeleton />
      <ScheduleItemSkeleton />
    </div>
  );

  const errorComponent = (
    errorMessage: string,
    action: () => void,
    actionMessage: string,
  ) => (
    <div className="tw-flex tw-flex-col tw-justify-center tw-items-center tw-h-full tw-space-y-2">
      <EmptyState
        description={errorMessage}
        icon={<AlertCircle className="tw-h-7 tw-w-7 tw-text-gray-500" />}
        secondaryActionOnClick={action}
        secondaryActionLabel={actionMessage}
      />
    </div>
  );

  useEffect(() => {
    if (isDailyScheduleError) {
      trackEvent("Daily Schedule Error", {
        error: "Error fetching full schedule",
      });
    }
    if (isGoogleCalendarError) {
      trackEvent("Google Calendar Error", {
        error: "Error fetching Google Calendar",
      });
    }
    if (isGoogleSyncError) {
      trackEvent("Google Sync Error", {
        error: "Error with Google Sync",
      });
    }
  }, [
    isDailyScheduleError,
    isGoogleCalendarError,
    isGoogleSyncError,
    trackEvent,
  ]);

  const maxScheduleItemsBeforeContainerIsScrollable = 5;

  return (
    <div className="tw-h-full tw-w-full tw-flex tw-flex-col tw-overflow-hidden">
      <h2 className="tw-text-lg tw-font-bold tw-text-gray-700 tw-mb-4">
        Schedule and Check-in
      </h2>
      <div className="tw-flex-1 tw-min-h-0">
        <Card className="tw-h-full tw-bg-white/75">
          <CardContent className="tw-pt-6 tw-h-full tw-overflow-hidden">
            <Tabs
              defaultValue="full-schedule"
              className="tw-h-full tw-flex tw-flex-col"
            >
              <TabsList className="tw-grid tw-w-full tw-grid-cols-2 tw-border tw-border-border">
                <TabsTrigger value="full-schedule">Full schedule</TabsTrigger>
                <TabsTrigger value="my-schedule">My day</TabsTrigger>
              </TabsList>
              <div
                className={cn(
                  "tw-flex-1 tw-min-h-0 tw-overflow-hidden",
                  dailySchedule.length >
                    maxScheduleItemsBeforeContainerIsScrollable &&
                    "tw-border-b",
                )}
              >
                <TabsContent
                  value="full-schedule"
                  className={cn("tw-h-full tw-overflow-y-auto")}
                >
                  <div className="tw-text-center">
                    {isDailyScheduleLoading && loadingSkeleton}

                    {isDailyScheduleError &&
                      errorComponent(
                        errorMessages.dailySchedule,
                        () => {
                          window.location.reload();
                        },
                        "Reload Page",
                      )}

                    {!isDailyScheduleLoading &&
                      !isDailyScheduleError &&
                      dailySchedule.length === 0 && (
                        <div className="tw-flex tw-flex-col tw-justify-center tw-items-center tw-h-full tw-space-y-2">
                          <p className="tw-text-lg tw-font-medium">
                            Nothing scheduled for today
                          </p>
                          <p className="tw-text-sm tw-text-gray-500">
                            {
                              "No classes, appointments, or events\nscheduled for today."
                            }
                          </p>
                        </div>
                      )}
                    {!isDailyScheduleLoading && dailySchedule.length > 0 && (
                      <>
                        <p className="tw-text-sm tw-text-gray-500 tw-pb-2">
                          View all upcoming classes, events, and appointments.
                        </p>
                        <div className="tw-space-y-4 tw-p-0.5">
                          {dailySchedule.map((item) => (
                            <ScheduleItem
                              key={item.id}
                              scheduleItem={item}
                              title={item.title}
                              startTime={item.startTime}
                              endTime={item.endTime}
                              formattedStartTime={item?.formattedStartTime}
                              formattedEndTime={item?.formattedEndTime}
                              link={item?.link}
                              attendanceCount={item?.attendanceCount}
                            />
                          ))}
                        </div>
                      </>
                    )}
                  </div>
                </TabsContent>
                <TabsContent
                  value="my-schedule"
                  className="tw-h-full tw-overflow-y-auto tw-vertical-fade"
                >
                  <div className="tw-text-center tw-py-4 tw-flex-grow tw-overflow-y-auto">
                    {userDailyScheduleLoading && loadingSkeleton}

                    {isGoogleCalendarConnected &&
                      isGoogleCalendarError &&
                      errorComponent(
                        errorMessages.googleCalendar,
                        () => {
                          window.location.reload();
                        },
                        "Reload Page",
                      )}

                    {isGoogleSyncError &&
                      errorComponent(
                        errorMessages.googleSync,
                        () => {
                          navigate(`/users/sync/${user.user_uuid}`);
                        },
                        "Check Google Sync",
                      )}

                    {!userDailyScheduleLoading &&
                      userDailySchedule.length === 0 &&
                      isGoogleCalendarConnected &&
                      !isGoogleCalendarError &&
                      !isGoogleSyncError && (
                        <div className="tw-flex tw-flex-col tw-justify-center tw-items-center tw-h-full tw-space-y-2">
                          <p className="tw-text-lg tw-font-medium">
                            Nothing on your schedule
                          </p>
                          <p className="tw-text-sm tw-text-gray-500">
                            No classes, appointments, or events <br /> scheduled
                            for you today.
                          </p>
                        </div>
                      )}
                    {!userDailyScheduleLoading &&
                      userDailySchedule.length > 0 && (
                        <div className="tw-space-y-4">
                          <p className="tw-text-sm tw-text-gray-500 tw-mb-4">
                            Your personal schedule for today.
                          </p>
                          {userDailySchedule.map((item) => (
                            <ScheduleItem
                              key={item.id}
                              scheduleItem={item}
                              title={item.title}
                              startTime={item.startTime}
                              endTime={item.endTime}
                              formattedStartTime={item?.formattedStartTime}
                              formattedEndTime={item?.formattedEndTime}
                              link={item.link}
                            />
                          ))}
                        </div>
                      )}
                  </div>
                  {!isGoogleCalendarConnected && (
                    <div className="tw-mt-4 tw-flex-shrink-0">
                      <div className="tw-flex tw-flex-col tw-items-center tw-space-y-4 tw-mt-4">
                        <p className="tw-text-sm tw-text-gray-500 tw-text-center">
                          Connect your Google Calendar to get <br /> a complete
                          snapshot of your day.
                        </p>

                        <Button
                          variant="default"
                          onClick={() =>
                            navigate(`/users/sync/${user.user_uuid}`)
                          }
                        >
                          Connect now
                          <ArrowRight className="tw-ml-2 tw-h-4 tw-w-4" />
                        </Button>
                      </div>
                    </div>
                  )}
                </TabsContent>
              </div>
            </Tabs>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}

export default MySchedule;
